import { useRef } from "react";
import type { ReactNode } from "react";
import { Quiz } from "./Quiz";
import { AudioTrack } from "./AudioTrack";
import { RoyalOath } from "./RoyalOath";
import { StickFigure } from "./StickFigure";
import { SecretChat } from "./SecretChat";
import "./index.css";

const INK = "#2b1d12";
const INK_SOFT = "#4a3a2a";
const GOLD = "#8a6a3b";
const PARCHMENT = "#faf5ec";
const RULE = "#e3d6bf";
const TOTAL_PAGES = 11;

function Label({ children }: { children: ReactNode }) {
  return (
    <div
      className="text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase"
      style={{ color: GOLD }}
    >
      {children}
    </div>
  );
}

function Divider({ className = "my-10" }: { className?: string }) {
  return (
    <div
      className={`mx-auto w-16 h-px ${className}`}
      style={{ backgroundColor: GOLD }}
    />
  );
}

function PartHeading({
  part,
  title,
  subtitle,
}: {
  part: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <header className="text-center mb-12 sm:mb-16">
      <div
        className="text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase mb-4"
        style={{ color: GOLD }}
      >
        {part}
      </div>
      <h2
        className="text-3xl sm:text-4xl italic leading-tight text-pretty"
        style={{ color: INK }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className="mt-4 text-sm sm:text-base italic px-2 text-pretty"
          style={{ color: INK_SOFT }}
        >
          {subtitle}
        </p>
      )}
      <Divider className="mt-8" />
    </header>
  );
}

function Section({
  id,
  nextId,
  page,
  children,
}: {
  id: string;
  nextId?: string;
  page?: number;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className="page-vignette px-5 sm:px-6 py-16 sm:py-20 md:py-24 border-t min-h-svh md:min-h-0 flex md:block flex-col scroll-mt-0"
      style={{ borderColor: RULE }}
    >
      <div className="flex-1 md:flex-none flex md:block items-center">
        <div className="max-w-2xl mx-auto w-full">{children}</div>
      </div>
      {nextId && (
        <a
          href={`#${nextId}`}
          className="md:hidden flex items-center justify-center gap-3 pt-8 py-3 -mb-3 text-[10px] tracking-[0.25em] uppercase"
          style={{ color: GOLD }}
        >
          {page != null && (
            <span style={{ opacity: 0.55 }}>
              {page}/{TOTAL_PAGES}
            </span>
          )}
          <span>
            Next page <span className="ml-1">↓</span>
          </span>
        </a>
      )}
    </section>
  );
}

function Decree({ roman, children }: { roman: string; children: ReactNode }) {
  return (
    <article>
      <h3
        className="italic text-sm tracking-[0.15em] uppercase mb-3"
        style={{ color: GOLD }}
      >
        Decree {roman}
      </h3>
      <p className="text-base leading-relaxed" style={{ color: INK_SOFT }}>
        {children}
      </p>
    </article>
  );
}

function ScrollTrigger() {
  const tapsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTap = () => {
    tapsRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (tapsRef.current >= 3) {
      tapsRef.current = 0;
      window.dispatchEvent(new CustomEvent("secret-chat:open"));
      return;
    }
    timerRef.current = setTimeout(() => {
      tapsRef.current = 0;
    }, 1500);
  };

  return (
    <button
      type="button"
      onClick={handleTap}
      aria-label="A private scroll"
      className="mt-8 p-2 transition-opacity"
      style={{ color: GOLD, opacity: 0.45 }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.45")}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <ellipse cx="4.5" cy="12" rx="2" ry="6" />
        <ellipse cx="19.5" cy="12" rx="2" ry="6" />
        <path d="M4.5 6 H19.5" />
        <path d="M4.5 18 H19.5" />
        <path d="M8.5 10 H15.5" />
        <path d="M8.5 13 H15.5" />
      </svg>
    </button>
  );
}

function Preference({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <article>
      <h3 className="italic text-xl mb-2 text-pretty" style={{ color: INK }}>
        {heading}
      </h3>
      <p className="text-base leading-relaxed" style={{ color: INK_SOFT }}>
        {children}
      </p>
    </article>
  );
}

export function App() {
  return (
    <div
      className="font-serif"
      style={{ backgroundColor: PARCHMENT, color: INK }}
    >
      <section
        id="cover"
        className="page-vignette min-h-svh flex flex-col items-center justify-center px-5 sm:px-6 py-20"
      >
        <div className="max-w-2xl w-full text-center">
          <Label>An Addendum to the Sacred Texts</Label>
          <h1
            className="mt-10 text-6xl sm:text-7xl md:text-8xl leading-none text-pretty"
            style={{ color: INK, fontWeight: 300 }}
          >
            Volume II
          </h1>
          <Divider className="mt-10 mb-8" />
          <p
            className="text-xl sm:text-2xl italic mb-12 px-2 text-pretty"
            style={{ color: INK_SOFT }}
          >
            Concerning Her Highness, Queen Farah
          </p>
          <p
            className="text-lg sm:text-xl leading-relaxed italic max-w-lg mx-auto text-pretty"
            style={{ color: INK_SOFT }}
          >
            Long may she reign.
          </p>
        </div>
        <div
          className="md:hidden mt-16 py-3 text-[10px] tracking-[0.25em] uppercase"
          style={{ color: GOLD }}
        >
          Page 1
        </div>
      </section>

      <Section id="dedication" nextId="contents" page={2}>
        <PartHeading part="Dedication" title="To the Author of the Sacred Text" />
        <div
          className="space-y-6 text-base leading-relaxed italic"
          style={{ color: INK_SOFT }}
        >
          <p>
            To Her Excellency Nada, author of the canonical guide of 2019.
          </p>
          <p>
            Six years ago, you wrote the scripture. It has not been bested and
            it will not be bested. This volume is not a replacement. It is a
            continuation of the historical record, updated for the Queen's
            newly ascended life in the Americas. Your work remains scripture.
            Consider this the apocrypha.
          </p>
          <p className="text-right not-italic" style={{ color: GOLD }}>
            To the Queen, قلبي. This one is yours, to amend or ignore as you
            see fit.
          </p>
        </div>
      </Section>

      <Section id="contents" nextId="part-i" page={3}>
        <PartHeading part="Table of Contents" title="The Seven Parts" />
        <ol className="space-y-4 text-base" style={{ color: INK_SOFT }}>
          {[
            ["I", "The Royal Decrees, Amended & Expanded", "part-i"],
            ["II", "Known Preferences of Her Highness", "part-ii"],
            ["III", "The Royal Menagerie", "part-iii"],
            ["IV", "The Queen's Inner Court", "part-iv"],
            ["V", "Chronicles of Her Highness", "part-v"],
            ["VI", "The Banished", "part-vi"],
            ["VII", "The Loyalty Examination", "part-vii"],
          ].map(([roman, title, href]) => (
            <li key={href} className="flex items-baseline gap-6">
              <span
                className="italic text-sm tracking-[0.2em] w-10"
                style={{ color: GOLD }}
              >
                {roman}.
              </span>
              <a
                href={`#${href}`}
                className="italic hover:underline"
                style={{ color: INK }}
              >
                {title}
              </a>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="part-i" nextId="part-ii" page={4}>
        <PartHeading
          part="Part I"
          title="The Royal Decrees"
          subtitle="Amended & Expanded"
        />
        <p
          className="text-base leading-relaxed italic mb-14 text-center"
          style={{ color: INK_SOFT }}
        >
          The original twelve rules of Respect remain in full force. The
          following are additions, clarifications, and regional updates for the
          Queen's Californian Era.
        </p>
        <div className="space-y-10">
          <Decree roman="XIII">
            The Queen has decreed that she is not an overthinker. You, however,
            must overthink enough for both of you. This is now your sacred
            burden.
          </Decree>
          <Decree roman="XIV">
            You shall not offer unsolicited solutions to Her Highness's
            problems. She is intelligent and solution-oriented and has already
            identified the answer before you opened your mouth. Your role is to
            listen, to validate, and to keep your phone face-down while she
            speaks. Checking your phone mid-sentence is grounds for
            excommunication.
          </Decree>
          <Decree roman="XV">
            When the Queen requires space, you shall grant it immediately and
            without negotiation. She will return. She always returns. Crowding
            her is the fastest known path to the dungeons.
          </Decree>
          <Decree roman="XVI">
            Physical touch is not the Queen's preferred love language. Hugs are
            granted by royal whim, not by request. Holding hands is tolerated
            as a favor to you. The approved form of contact is the arm-hold
            while walking, which must be kept straight, parallel to the ground,
            and unwavering. Failure to maintain proper elbow geometry is a
            minor offense.
          </Decree>
          <Decree roman="XVII">
            Shoulder rubs and back cracks may be offered only by those who are
            certain of their skill. The Queen demands precision pressure: too
            soft is insulting, too hard is assault. You must solve for
            equilibrium. There is no partial credit.
          </Decree>
          <Decree roman="XVIII">
            The Queen's social battery is not a battery. It is a flame. Some
            gatherings extinguish it within minutes. You shall monitor the
            flame at all times and extract the Queen from any environment in
            which it begins to flicker.
          </Decree>
        </div>
      </Section>

      <Section id="part-ii" nextId="part-iii" page={5}>
        <PartHeading
          part="Part II"
          title="Known Preferences of Her Highness"
        />
        <div className="space-y-10">
          <Preference heading="On Flora">
            The Dahlia is the sacred bloom of the realm. All other flowers may
            be offered as tribute, but none carry the weight of the Dahlia.
          </Preference>
          <Preference heading="On Water">
            The Queen drinks Voss. The Fiji Era ended by conscience. The Evian
            Era ended by evolution. Tap water is a personal insult and shall
            not be offered under any circumstance. Generic bottled water is
            tolerated only in emergencies and will be noted.
          </Preference>
          <Preference heading="On Shawarma">
            The Queen's favorite food is chicken shawarma, but not all shawarma
            is worthy. The acceptable shawarma is long, grilled after assembly
            until toasted, served with tzatziki and generous garlic sauce.
            Pita-wrapped shawarma is permissible but inferior. Hummus-forward
            shawarma is a disappointment.
          </Preference>
          <Preference heading="On Chick-fil-A">
            The order is scripture: Spicy Chicken Deluxe meal, Frosted Coffee.
            Do not deviate. Do not suggest alternatives. The Frosted Coffee in
            particular is beloved and must never be forgotten.
          </Preference>
          <Preference heading="On Spice">
            Her Highness favors heat in all things. When ordering on her
            behalf, err on the side of extra spicy or arrange for a worthy hot
            sauce to accompany the dish.
          </Preference>
          <Preference heading="On Burgers">
            Cheeseburgers, yes. Bacon, never. In-N-Out has been personally and
            officially rejected by the Crown. Do not bring it up.
          </Preference>
          <Preference heading="On Forbidden Foods">
            Pesto is denied. Bananas are denied. Peaches are not merely denied,
            they are a medical hazard and shall not cross the threshold of any
            royal residence.
          </Preference>
          <Preference heading="On Beverages">
            Drinks shall be half-sweet or less. Caramel macchiatos and vanilla
            lattes are in current rotation. Hot drinks must be hot. Tea shall
            be prepared with a tiny amount of honey and fresh mint. Oat milk is
            acceptable but regular milk remains the preferred option.
          </Preference>
          <Preference heading="On Pasta">
            The Queen loves pasta. The Queen claims she can cook pasta. These
            are two separate matters and shall not be conflated. She has, on
            record, burnt brownies. The kitchen is not her throne room.
          </Preference>
          <Preference heading="On Media">
            Her Highness has canonically identified as Kuzco. The former court
            figure known as <em>the ex</em> has been miscast as Yzma and
            banished to an appropriate pit. Sacred texts of the realm include
            Avatar: The Last Airbender, SpongeBob SquarePants, the original
            Marvel cycle, and any film that is either action, comedy, or
            horror. The Queen has no patience for slow cinema.
          </Preference>
          <article>
            <h3
              className="italic text-xl mb-2 text-pretty"
              style={{ color: INK }}
            >
              On Music
            </h3>
            <p
              className="text-base leading-relaxed"
              style={{ color: INK_SOFT }}
            >
              Her Highness possesses what can only be described as the finest
              taste in the realm. The catalog includes Green Day, rock in its
              uncompromised forms, Arabic song, the acoustic, and the quietly
              competent. One entry from the current rotation, set below for
              the record.
            </p>
            <AudioTrack
              src="/audio/ordinary-world.mp3"
              title="Ordinary World"
              artist="Green Day"
            />
          </article>
          <Preference heading="On Vocabulary">
            Certain dialects have emerged within the inner court. The consort
            may be addressed as <em>bart</em>, or, in moments of elevated
            affection, <em>bart bart</em>. The etymology has been sealed.
            Outsiders who inquire shall be redirected elsewhere.
          </Preference>
        </div>
      </Section>

      <Section id="part-iii" nextId="part-iv" page={6}>
        <PartHeading
          part="Part III"
          title="The Royal Menagerie"
          subtitle="Two creatures share the Queen's home. Both are noble. Neither is fully civilized."
        />
        <div className="space-y-10">
          <Preference heading="Milo, First of His Name · The Small One">
            Milo is not to be given human food under any circumstance. His
            rations are one scoop of the small kibble at dawn and one at dusk,
            occasionally halved at the Queen's discretion. He is prone to
            choking if permitted to drink water too quickly, so his hydration
            must be observed. Milo may be permitted on blankets only after
            having been freshly bathed. He is not permitted on couches. These
            are ancient laws.
          </Preference>
          <Preference heading="Koda · The Large One · The Slobberer">
            Koda receives two scoops of his dedicated large-breed food. He is
            not to be babied. Before any food reaches him, he must sit. He
            must wait. He must earn the meal. Koda is to be fed separately
            from Milo to prevent diplomatic incidents. Koda slobbers on all
            surfaces, including the Queen, and this is simply part of the
            arrangement.
          </Preference>
        </div>
      </Section>

      <Section id="part-iv" nextId="part-v" page={7}>
        <PartHeading
          part="Part IV"
          title="The Queen's Inner Court"
          subtitle="Her true court is small. Those admitted are cherished with a ferocity outsiders cannot perceive."
        />
        <div className="space-y-10">
          <Preference heading="Nada">
            Author of the Sacred Text of 2019. Her word carries the force of
            law, and any dispute of precedent is settled by the original
            scroll. Should she find this volume mistaken on any point, she may
            overrule it by raised eyebrow alone.
          </Preference>
          <Preference heading="Nayiri">
            The Queen's audience list runs two names deep. This is the second.
            The council is not a large institution, and it is not hiring.
          </Preference>
          <Preference heading="The House of Khaled">
            Mother Nelly. Father Khaled, whose first name became the Queen's
            surname, by the customs of the realm, the American system of
            nomenclature being insufficient to the task. Three brothers, two
            younger and one elder, Her Highness being the sole and therefore
            ruling daughter.
          </Preference>
        </div>
      </Section>

      <Section id="part-v" nextId="part-vi" page={8}>
        <PartHeading part="Part V" title="Chronicles of Her Highness" />
        <div
          className="space-y-6 text-base leading-relaxed"
          style={{ color: INK_SOFT }}
        >
          <p>
            The Queen was born in Egypt, raised in Bahrain, and schooled in
            France. In 2022 she made a brief exploratory expedition to the
            United States, returned to France to finish what she had started,
            and then crossed back to the Americas in 2024 with no intention of
            the matter being raised again.
          </p>
          <p>
            In September of 2025, before her twenty-seventh year, she took
            possession of her own estate in the territory of Antioch,
            California. The conquest was notable for being completed without a
            carriage of any kind. The Queen has declined to acquire an
            ordinary vehicle; a Jaguar waits for her in Bahrain, quietly, in
            the manner of a weapon not yet drawn.
          </p>
          <p>
            She practices civil law, which in practical terms means she brings
            the corporate realms to account on behalf of their underpaid
            subjects. She is presently at war with the fast-food empire of
            McDonald's over the matter of unpaid hours. The empire is losing.
          </p>
          <p>
            She has been on horseback since the age of six and competes in
            polo, being partial to the equestrian sport in which someone can
            clearly lose.
          </p>
        </div>
      </Section>

      <Section id="part-vi" nextId="part-vii" page={9}>
        <PartHeading
          part="Part VI"
          title="The Banished"
          subtitle="Names preserved only as warning."
        />
        <div className="space-y-10">
          <Preference heading="Joshua, also known as Josh">
            Josh once held a clerk's post at a firm now equally fallen from
            the Queen's favor. His crimes: the solicitation of improper
            imagery from Her Highness, the issuing of commentary entirely
            unrequested, and the conduct of these offenses while bound by the
            solemn vows of another household, a simultaneous trespass against
            two realms. He has been stripped of name, post, and welcome.
            Henceforth, any courtier observed behaving in like manner shall be
            deemed a <em>Josh</em>, and treated accordingly. Let the
            banishment stand as warning.
          </Preference>
        </div>
      </Section>

      <Section id="part-vii" nextId="colophon" page={10}>
        <PartHeading
          part="Part VII"
          title="The Loyalty Examination"
          subtitle="Twenty questions. Your title shall be conferred upon completion."
        />
        <Quiz />
      </Section>

      <footer
        id="colophon"
        className="page-vignette min-h-svh md:min-h-0 flex flex-col items-center justify-center text-center py-20 px-6 border-t"
        style={{ borderColor: RULE }}
      >
        <p
          className="text-base italic leading-relaxed"
          style={{ color: INK_SOFT }}
        >
          Set down with love by Sam,
          <br />
          <strong className="font-semibold" style={{ color: INK }}>
            her loyal subject, her bart
          </strong>
          .
        </p>
        <ScrollTrigger />
      </footer>
      <StickFigure />
      <RoyalOath />
      <SecretChat />
    </div>
  );
}

export default App;
